/**
 * Color Scheme definitions for per-section UI customization.
 * Each UI section can be set to Light, Dark, Glass, or Custom.
 */

export type ColorScheme = 'light' | 'dark' | 'glass' | 'hk' | 'custom';

export type SectionId = 'userNav' | 'userBnav' | 'userPage' | 'adminNav' | 'adminPage' | 'authPage';

export interface NavColorProps {
  bg: string;
  text: string;
  icon: string;
  activeText: string;
  activeBg: string;
  hoverBg: string;
  border: string;
}

export interface PageColorProps {
  bg: string;
  text: string;
  cardBg: string;
  cardBorder: string;
  heading: string;
  accent: string;
  accentText: string;
}

export interface AuthPageColorProps {
  bg: string;
  cardBg: string;
  cardBorder: string;
  heading: string;
  text: string;
  inputBg: string;
  inputBorder: string;
  inputText: string;
  buttonBg: string;
  buttonText: string;
  accent: string;
}

export type SectionColorProps = NavColorProps | PageColorProps | AuthPageColorProps;

export const SECTION_LABELS: Record<SectionId, { en: string; he: string }> = {
  userNav: { en: 'User: Side Nav', he: 'ניווט צד (משתמש)' },
  userBnav: { en: 'User: Bottom Nav', he: 'ניווט תחתון (משתמש)' },
  userPage: { en: 'User: Pages', he: 'דפים (משתמש)' },
  adminNav: { en: 'Admin: Nav', he: 'ניווט (ניהול)' },
  adminPage: { en: 'Admin: Pages', he: 'דפים (ניהול)' },
  authPage: { en: 'Auth: Login / Register / Onboarding', he: 'התחברות / הרשמה / אונבורדינג' },
};

/** Properties each section type supports */
export const NAV_PROPS = ['bg', 'text', 'icon', 'activeText', 'activeBg', 'hoverBg', 'border'] as const;
export const PAGE_PROPS = ['bg', 'text', 'cardBg', 'cardBorder', 'heading', 'accent', 'accentText'] as const;
export const AUTH_PAGE_PROPS = ['bg', 'cardBg', 'cardBorder', 'heading', 'text', 'inputBg', 'inputBorder', 'inputText', 'buttonBg', 'buttonText', 'accent'] as const;

export const SECTION_PROPS: Record<SectionId, readonly string[]> = {
  userNav: NAV_PROPS,
  userBnav: NAV_PROPS,
  userPage: PAGE_PROPS,
  adminNav: NAV_PROPS,
  adminPage: PAGE_PROPS,
  authPage: AUTH_PAGE_PROPS,
};

/** Human-readable labels for color properties */
export const PROP_LABELS: Record<string, { en: string; he: string }> = {
  bg: { en: 'Background', he: 'רקע' },
  text: { en: 'Text', he: 'טקסט' },
  icon: { en: 'Icon', he: 'אייקון' },
  activeText: { en: 'Active Text', he: 'טקסט פעיל' },
  activeBg: { en: 'Active Background', he: 'רקע פעיל' },
  hoverBg: { en: 'Hover Background', he: 'רקע מעבר' },
  border: { en: 'Border', he: 'מסגרת' },
  cardBg: { en: 'Card Background', he: 'רקע כרטיס' },
  cardBorder: { en: 'Card Border', he: 'מסגרת כרטיס' },
  heading: { en: 'Heading', he: 'כותרת' },
  accent: { en: 'Accent / Links', he: 'הדגשה / קישורים' },
  accentText: { en: 'Accent Text', he: 'טקסט הדגשה' },
  inputBg: { en: 'Input Background', he: 'רקע שדה' },
  inputBorder: { en: 'Input Border', he: 'מסגרת שדה' },
  inputText: { en: 'Input Text', he: 'טקסט שדה' },
  buttonBg: { en: 'Button Background', he: 'רקע כפתור' },
  buttonText: { en: 'Button Text', he: 'טקסט כפתור' },
};

const LIGHT_NAV: NavColorProps = {
  bg: '#F1F2F5',
  text: 'rgba(0,0,0,0.85)',
  icon: 'rgba(0,0,0,0.85)',
  activeText: '#007AFF',
  activeBg: '#EBF5FF',
  hoverBg: '#F9FAFB',
  border: '#E5E7EB',
};

const DARK_NAV: NavColorProps = {
  bg: '#1C1C1E',
  text: '#FFFFFF',
  icon: '#FFFFFF',
  activeText: '#70B8FF',
  activeBg: 'rgba(0,122,255,0.15)',
  hoverBg: '#2C2C2E',
  border: '#3A3A3C',
};

const GLASS_NAV: NavColorProps = {
  bg: 'rgba(255,255,255,0.78)',
  text: 'rgba(0,0,0,1)',
  icon: 'rgba(0,0,0,0.7)',
  activeText: '#007AFF',
  activeBg: 'rgba(0,122,255,0.1)',
  hoverBg: 'rgba(255,255,255,0.3)',
  border: 'rgba(255,255,255,0.45)',
};

const LIGHT_PAGE: PageColorProps = {
  bg: '#F1F2F5',
  text: 'rgba(0,0,0,0.85)',
  cardBg: '#FFFFFF',
  cardBorder: '#E5E7EB',
  heading: '#1e293b',
  accent: '#1a4fa0',
  accentText: '#FFFFFF',
};

const DARK_PAGE: PageColorProps = {
  bg: '#1C1C1E',
  text: '#FFFFFF',
  cardBg: '#2C2C2E',
  cardBorder: '#3A3A3C',
  heading: '#FFFFFF',
  accent: '#70B8FF',
  accentText: '#000000',
};

const GLASS_PAGE: PageColorProps = {
  bg: 'transparent',
  text: 'rgba(0,0,0,1)',
  cardBg: 'rgba(255,255,255,0.40)',
  cardBorder: 'rgba(255,255,255,0.45)',
  heading: '#000000',
  accent: '#007AFF',
  accentText: '#FFFFFF',
};

/** HK / Terminator theme — Deep Space Purple + Neon Mint */
const HK_NAV: NavColorProps = {
  bg: '#0A0A0A',
  text: '#E6E6E6',
  icon: '#07E5B0',
  activeText: '#07E5B0',
  activeBg: 'rgba(7,229,176,0.12)',
  hoverBg: '#2F2F2F',
  border: 'rgba(7,229,176,0.15)',
};

const HK_PAGE: PageColorProps = {
  bg: '#0A0A0A',
  text: '#E6E6E6',
  cardBg: '#252525',
  cardBorder: 'rgba(7,229,176,0.12)',
  heading: '#FFFFFF',
  accent: '#07E5B0',
  accentText: '#FFFFFF',
};

/** Auth page presets */
const LIGHT_AUTH: AuthPageColorProps = {
  bg: 'rgba(255,255,255,0.55)',
  cardBg: 'rgba(255,255,255,0.65)',
  cardBorder: 'rgba(255,255,255,0.4)',
  heading: '#0d2b6b',
  text: '#1a4fa0',
  inputBg: 'rgba(255,255,255,0.7)',
  inputBorder: 'rgba(255,255,255,0.5)',
  inputText: '#0d2b6b',
  buttonBg: '#1a4fa0',
  buttonText: '#ffffff',
  accent: '#1a4fa0',
};

const DARK_AUTH: AuthPageColorProps = {
  bg: 'rgba(0,0,0,0.7)',
  cardBg: 'rgba(30,30,30,0.85)',
  cardBorder: 'rgba(255,255,255,0.1)',
  heading: '#FFFFFF',
  text: 'rgba(255,255,255,0.7)',
  inputBg: 'rgba(255,255,255,0.08)',
  inputBorder: 'rgba(255,255,255,0.15)',
  inputText: '#FFFFFF',
  buttonBg: '#3b8dd4',
  buttonText: '#FFFFFF',
  accent: '#70B8FF',
};

const GLASS_AUTH: AuthPageColorProps = {
  bg: 'rgba(255,255,255,0.4)',
  cardBg: 'rgba(255,255,255,0.5)',
  cardBorder: 'rgba(255,255,255,0.45)',
  heading: '#000000',
  text: 'rgba(0,0,0,0.6)',
  inputBg: 'rgba(255,255,255,0.5)',
  inputBorder: 'rgba(255,255,255,0.4)',
  inputText: '#000000',
  buttonBg: '#007AFF',
  buttonText: '#FFFFFF',
  accent: '#007AFF',
};

const HK_AUTH: AuthPageColorProps = {
  bg: 'rgba(10,10,10,0.96)',
  cardBg: 'rgba(37,37,37,0.92)',
  cardBorder: 'rgba(7,229,176,0.15)',
  heading: '#FFFFFF',
  text: '#E6E6E6',
  inputBg: 'rgba(255,255,255,0.06)',
  inputBorder: 'rgba(7,229,176,0.15)',
  inputText: '#FFFFFF',
  buttonBg: '#07E5B0',
  buttonText: '#0A0A0A',
  accent: '#07E5B0',
};

type PresetMap = {
  userNav: NavColorProps;
  userBnav: NavColorProps;
  userPage: PageColorProps;
  adminNav: NavColorProps;
  adminPage: PageColorProps;
  authPage: AuthPageColorProps;
};

export const SCHEME_PRESETS: Record<Exclude<ColorScheme, 'custom'>, PresetMap> = {
  light: {
    userNav: LIGHT_NAV,
    userBnav: LIGHT_NAV,
    userPage: LIGHT_PAGE,
    adminNav: LIGHT_NAV,
    adminPage: LIGHT_PAGE,
    authPage: LIGHT_AUTH,
  },
  dark: {
    userNav: DARK_NAV,
    userBnav: DARK_NAV,
    userPage: DARK_PAGE,
    adminNav: DARK_NAV,
    adminPage: DARK_PAGE,
    authPage: DARK_AUTH,
  },
  glass: {
    userNav: GLASS_NAV,
    userBnav: GLASS_NAV,
    userPage: GLASS_PAGE,
    adminNav: GLASS_NAV,
    adminPage: GLASS_PAGE,
    authPage: GLASS_AUTH,
  },
  hk: {
    userNav: HK_NAV,
    userBnav: HK_NAV,
    userPage: HK_PAGE,
    adminNav: HK_NAV,
    adminPage: HK_PAGE,
    authPage: HK_AUTH,
  },
};

/** Maps section.prop -> CSS variable name */
const CSS_VAR_MAP: Record<string, string> = {
  'userNav.bg': '--user-nav-bg',
  'userNav.text': '--user-nav-text',
  'userNav.icon': '--user-nav-icon',
  'userNav.activeText': '--user-nav-active-text',
  'userNav.activeBg': '--user-nav-active-bg',
  'userNav.hoverBg': '--user-nav-hover-bg',
  'userNav.border': '--user-nav-border',

  'userBnav.bg': '--user-bnav-bg',
  'userBnav.text': '--user-bnav-text',
  'userBnav.icon': '--user-bnav-icon',
  'userBnav.activeText': '--user-bnav-active-text',
  'userBnav.activeBg': '--user-bnav-active-bg',
  'userBnav.hoverBg': '--user-bnav-hover-bg',
  'userBnav.border': '--user-bnav-border',

  'userPage.bg': '--user-page-bg',
  'userPage.text': '--user-page-text',
  'userPage.cardBg': '--user-page-card-bg',
  'userPage.cardBorder': '--user-page-card-border',
  'userPage.heading': '--user-page-heading',
  'userPage.accent': '--user-page-accent',
  'userPage.accentText': '--user-page-accent-text',

  'adminNav.bg': '--admin-nav-bg',
  'adminNav.text': '--admin-nav-text',
  'adminNav.icon': '--admin-nav-icon',
  'adminNav.activeText': '--admin-nav-active-text',
  'adminNav.activeBg': '--admin-nav-active-bg',
  'adminNav.hoverBg': '--admin-nav-hover-bg',
  'adminNav.border': '--admin-nav-border',

  'adminPage.bg': '--admin-page-bg',
  'adminPage.text': '--admin-page-text',
  'adminPage.cardBg': '--admin-page-card-bg',
  'adminPage.cardBorder': '--admin-page-card-border',
  'adminPage.heading': '--admin-page-heading',
  'adminPage.accent': '--admin-page-accent',
  'adminPage.accentText': '--admin-page-accent-text',

  'authPage.bg': '--auth-page-bg',
  'authPage.cardBg': '--auth-page-card-bg',
  'authPage.cardBorder': '--auth-page-card-border',
  'authPage.heading': '--auth-page-heading',
  'authPage.text': '--auth-page-text',
  'authPage.inputBg': '--auth-page-input-bg',
  'authPage.inputBorder': '--auth-page-input-border',
  'authPage.inputText': '--auth-page-input-text',
  'authPage.buttonBg': '--auth-page-button-bg',
  'authPage.buttonText': '--auth-page-button-text',
  'authPage.accent': '--auth-page-accent',
};

export const SECTION_IDS: SectionId[] = ['userNav', 'userBnav', 'userPage', 'adminNav', 'adminPage', 'authPage'];

/** Settings key for a section's scheme */
export function schemeKey(section: SectionId): string {
  return `colors.${section}.scheme`;
}

/** Settings key for a section's color property */
export function colorKey(section: SectionId, prop: string): string {
  return `colors.${section}.${prop}`;
}

/** Resolve the actual color values for a section based on scheme + custom overrides */
export function resolveColors(
  section: SectionId,
  settings: Record<string, string>,
): Record<string, string> {
  const scheme = (settings[schemeKey(section)] || 'hk') as ColorScheme;
  const props = SECTION_PROPS[section];

  if (scheme !== 'custom') {
    const preset = SCHEME_PRESETS[scheme][section] as unknown as Record<string, string>;
    const result: Record<string, string> = {};
    for (const prop of props) {
      result[prop] = preset[prop];
    }
    return result;
  }

  // Custom: read each color from settings, fallback to light preset
  const fallback = SCHEME_PRESETS.light[section] as unknown as Record<string, string>;
  const result: Record<string, string> = {};
  for (const prop of props) {
    result[prop] = settings[colorKey(section, prop)] || fallback[prop];
  }
  return result;
}

/** Apply all color settings as CSS variables on the root element */
export function applyColorSettings(settings: Record<string, string>, root: HTMLElement): void {
  for (const section of SECTION_IDS) {
    const colors = resolveColors(section, settings);
    for (const [prop, value] of Object.entries(colors)) {
      const cssVar = CSS_VAR_MAP[`${section}.${prop}`];
      if (cssVar) {
        root.style.setProperty(cssVar, value);
      }
    }
  }
  // Bridge variables for glass classes (needed by portaled modals outside AppLayout)
  root.style.setProperty('--page-card-bg', 'var(--user-page-card-bg)');
  root.style.setProperty('--page-card-border', 'var(--user-page-card-border)');
}

/** Generate the flat settings keys with HK defaults (for backend DEFAULT_SETTINGS) */
export function generateDefaultSettingsKeys(): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const section of SECTION_IDS) {
    defaults[schemeKey(section)] = 'hk';
    const preset = SCHEME_PRESETS.hk[section] as unknown as Record<string, string>;
    for (const prop of SECTION_PROPS[section]) {
      defaults[colorKey(section, prop)] = preset[prop];
    }
  }
  return defaults;
}
